import React from "react";
import {
  Box,
  Container,
  Typography,
  Paper,
  Divider,
  Link,
} from "@mui/material";
import ControlsBar from "./ControlsBar";
import { useLanguage } from "../contexts/LanguageContext";
import SEOHead from "./SEOHead";

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <Box sx={{ mb: 4 }}>
    <Typography
      variant="h6"
      sx={{ fontWeight: 700, mb: 1.5, color: "primary.main" }}
    >
      {title}
    </Typography>
    {children}
  </Box>
);

const PrivacyPolicy: React.FC = () => {
  const { isRTL } = useLanguage();
  return (
    <>
      <SEOHead
        title="Privacy Policy | my-aitools.online"
        description="Privacy policy for my-aitools.online. We do not store or log any personal data you submit to our AI tools. Your information is processed in real time and immediately discarded."
        canonicalUrl="https://my-aitools.online/resume-optimizer/#/privacy-policy"
        noIndex={false}
      />
      <ControlsBar />
      <Box sx={{ minHeight: "100vh", backgroundColor: "background.default", py: 6 }}>
        <Container maxWidth="md">
          <Link
            href="#/"
            underline="hover"
            sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, mb: 4, color: "primary.main", fontWeight: 600, fontSize: "0.95rem" }}
          >
            {isRTL ? "→" : "←"} Back to Resume Optimizer
          </Link>

          <Paper sx={{ p: { xs: 3, sm: 5 }, borderRadius: 3, backgroundColor: "background.paper" }}>
            <Typography variant="h4" sx={{ fontWeight: 800, mb: 0.5, letterSpacing: "-0.02em" }}>
              Privacy Policy
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Last updated:{" "}
              {new Date().toLocaleDateString("en-US", { month: "long", year: "numeric" })}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
              This policy applies to <strong>my-aitools.online</strong> and all tools hosted on it,
              including the Resume Optimizer, AI Document Writer, and any future tools ("the Site", "we", "us").
            </Typography>

            <Divider sx={{ mb: 4 }} />

            <Section title="1. Overview">
              <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                We take your privacy seriously. All tools on my-aitools.online are designed with a
                strict no-storage policy: <strong>we do not store, save, or retain any personal
                information you submit</strong>. Any text you provide — whether a resume, job description,
                letter content, or personal details — is processed in real time and immediately discarded
                after the response is returned to you.
              </Typography>
            </Section>

            <Section title="2. What Information You Submit">
              <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8, mb: 1.5 }}>
                Depending on which tool you use, you may voluntarily submit:
              </Typography>
              <Box component="ul" sx={{ pl: 3, color: "text.secondary", lineHeight: 2 }}>
                <li>Resume text (pasted or extracted from a PDF/TXT file)</li>
                <li>Job descriptions</li>
                <li>Personal details for document generation (name, email, phone)</li>
                <li>Letter content and context for AI drafting</li>
              </Box>
              <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8, mt: 1.5 }}>
                This information is sent to our backend server solely to generate your output.
                It is <strong>not logged, stored in any database, or accessible to anyone</strong> —
                including the site owner.
              </Typography>
            </Section>

            <Section title="3. How Your Data Is Processed">
              <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                Input you provide is forwarded to <strong>DeepInfra</strong> (deepinfra.com),
                a third-party AI API provider, to generate your result. DeepInfra processes this
                data according to their own{" "}
                <Link href="https://deepinfra.com/privacy" target="_blank" rel="noopener noreferrer" color="primary">
                  Privacy Policy
                </Link>
                . We recommend reviewing it if you have concerns about how AI providers handle input data.
              </Typography>
            </Section>

            <Section title="4. Data We Do NOT Collect">
              <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8, mb: 1.5 }}>
                We do <strong>not</strong> collect or store:
              </Typography>
              <Box component="ul" sx={{ pl: 3, color: "text.secondary", lineHeight: 2 }}>
                <li>Your name, email address, or any contact information on our servers</li>
                <li>Any document or resume content after your session ends</li>
                <li>IP addresses tied to your submissions</li>
                <li>User accounts or profiles</li>
                <li>Payment information (all tools are free to use)</li>
              </Box>
            </Section>

            <Section title="5. Local Storage (Your Browser)">
              <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                To improve your experience, some tools store the following locally{" "}
                <strong>in your own browser</strong> (never on our servers):
              </Typography>
              <Box component="ul" sx={{ pl: 3, color: "text.secondary", lineHeight: 2 }}>
                <li><strong>Theme preference</strong> (light / dark mode)</li>
                <li><strong>Language preference</strong> (English / Arabic)</li>
                <li><strong>Resume text and job description</strong> — so you don't lose your work on refresh</li>
                <li><strong>Basic contact info</strong> (name, email, phone) in the Document Writer — for convenience when generating multiple documents</li>
              </Box>
              <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8, mt: 1.5 }}>
                You can clear this data at any time by clearing your browser's local storage,
                or using the "Clear saved data" button within the tool.
              </Typography>
            </Section>

            <Section title="6. Cookies">
              <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                This Site does not use tracking cookies of its own. We may display Google AdSense
                advertisements, which may use cookies to show relevant ads based on your browsing history.
                You can opt out of personalized advertising via{" "}
                <Link href="https://www.google.com/settings/ads" target="_blank" rel="noopener noreferrer" color="primary">
                  Google's Ad Settings
                </Link>.
              </Typography>
            </Section>

            <Section title="7. Third-Party Services">
              <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8, mb: 1.5 }}>
                The Site relies on the following third-party services:
              </Typography>
              <Box component="ul" sx={{ pl: 3, color: "text.secondary", lineHeight: 2 }}>
                <li><strong>DeepInfra</strong> — AI model API (processes your submitted text to generate outputs)</li>
                <li><strong>Render.com</strong> — cloud hosting for our backend server</li>
                <li><strong>Google AdSense</strong> — may display ads on the Site</li>
                <li><strong>Google Fonts</strong> — used for typography in generated PDFs</li>
              </Box>
            </Section>

            <Section title="8. Children's Privacy">
              <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                This Site is not directed at children under 13 years of age. We do not knowingly
                collect any information from children.
              </Typography>
            </Section>

            <Section title="9. Changes to This Policy">
              <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                We may update this Privacy Policy from time to time. Any changes will be posted
                on this page with an updated date at the top. Continued use of the Site after
                changes constitutes acceptance of the updated policy.
              </Typography>
            </Section>

            <Section title="10. Contact">
              <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                If you have any questions about this Privacy Policy, please contact us at:{" "}
                <Link href="mailto:management@my-aitools.online" color="primary">
                  management@my-aitools.online
                </Link>
              </Typography>
            </Section>

            <Divider sx={{ mt: 2, mb: 3 }} />

            <Typography variant="body2" color="text.secondary" align="center">
              © {new Date().getFullYear()} my-aitools.online — All rights reserved.
            </Typography>
          </Paper>
        </Container>
      </Box>
    </>
  );
};

export default PrivacyPolicy;
